import {
  endOfMonth,
  endOfWeek,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { NonBrandMetricEvolutionPoint, NonBrandMetricEvolutionResponse } from 'src/context/api/non-branded/models';

export type MetricKey = Exclude<keyof NonBrandMetricEvolutionPoint, 'date_interval'>;
export type Granularity = 'weekly' | 'monthly';
export type CompareMode = 'none' | 'previous_period' | 'year_over_year';
export type DateRange = { start: Date; end: Date };

export type MetricDefinition = {
  key: MetricKey;
  /** ExploreChart format (`kind:decimals`): picks the y axis family and the value formatter. */
  format: string;
  /** Used by ExploreChart for the metrics it does not paint itself as paid (yellow) or organic (green). */
  color: string;
  /** Search volumes are monthly: no weekly value. */
  monthlyOnly?: boolean;
  /** Same workspace-type split as the grid columns (ROAS/AOV eCommerce, CPA/CPC Lead). */
  template?: 'ecommerce' | 'lead';
};

/** The maquette's metric list, in its order. */
const METRICS: MetricDefinition[] = [
  { key: 'non_brand_searches', format: 'float:0', color: '#F08B77', monthlyOnly: true },
  { key: 'paid_search_sov', format: 'percentage:1', color: '#DAA335' },
  { key: 'paid_shopping_sov', format: 'percentage:1', color: '#DAA335' },
  { key: 'organic_sov', format: 'percentage:1', color: '#18A155' },
  { key: 'visibility_rate', format: 'percentage:1', color: '#1E88E5' },
  { key: 'paid_search_vr', format: 'percentage:1', color: '#DAA335' },
  { key: 'paid_shopping_vr', format: 'percentage:1', color: '#DAA335' },
  { key: 'organic_vr', format: 'percentage:1', color: '#18A155' },
  { key: 'organic_position', format: 'float:1', color: '#18A155' },
  { key: 'blended_ctr', format: 'percentage:1', color: '#5119B7', monthlyOnly: true },
  { key: 'organic_ctr', format: 'percentage:1', color: '#18A155' },
  { key: 'paid_search_ctr', format: 'percentage:1', color: '#DAA335' },
  { key: 'paid_shopping_ctr', format: 'percentage:1', color: '#DAA335' },
  { key: 'non_brand_traffic', format: 'float:0', color: '#637381' },
  { key: 'ad_cost', format: 'currency:0', color: '#DAA335' },
  { key: 'paid_roas', format: 'float:2', color: '#DAA335', template: 'ecommerce' },
  { key: 'paid_aov', format: 'currency:2', color: '#DAA335', template: 'ecommerce' },
  { key: 'paid_cpa', format: 'currency:2', color: '#DAA335', template: 'lead' },
  { key: 'paid_cpc', format: 'currency:2', color: '#DAA335', template: 'lead' },
  { key: 'non_brand_conversions', format: 'float:0', color: '#0052B4' },
  { key: 'non_brand_revenue', format: 'currency:0', color: '#0B6E4F' },
  { key: 'cvr', format: 'percentage:1', color: '#006C9C' },
];

export const DEFAULT_METRICS: MetricKey[] = ['non_brand_searches', 'ad_cost'];

export const metricLabelKey = (key: MetricKey) => `dashboard.nb_brand_grid.metric_evolution.metrics.${key}`;

export const metricsForTemplate = (isLead: boolean): MetricDefinition[] =>
  METRICS.filter((metric) => !metric.template || metric.template === (isLead ? 'lead' : 'ecommerce'));

export const isAvailableAt = (key: MetricKey, granularity: Granularity): boolean =>
  granularity === 'monthly' || !METRICS.find((metric) => metric.key === key)?.monthlyOnly;

/** Last `amount` weeks (Monday to Sunday) or months, the current one included, like the Brand Metric evolution modals. */
export function analysisRange(granularity: Granularity, amount: number, today: Date = new Date()): DateRange {
  if (granularity === 'monthly') {
    return { start: subMonths(startOfMonth(today), amount - 1), end: endOfMonth(today) };
  }
  return {
    start: subWeeks(startOfWeek(today, { weekStartsOn: 1 }), amount - 1),
    end: endOfWeek(today, { weekStartsOn: 1 }),
  };
}

/**
 * Range compared with: the `amount` buckets right before, or the same ones a year earlier (52 weeks back, so that
 * weeks still start on Monday). Always as many buckets as the main range: the chart pairs them by position.
 */
export function compareRange(range: DateRange, granularity: Granularity, amount: number, mode: CompareMode): DateRange | null {
  if (mode === 'none') return null;
  if (granularity === 'monthly') {
    const shift = mode === 'previous_period' ? amount : 12;
    return { start: subMonths(range.start, shift), end: endOfMonth(subMonths(range.end, shift)) };
  }
  const shift = mode === 'previous_period' ? amount : 52;
  return { start: subWeeks(range.start, shift), end: subWeeks(range.end, shift) };
}

/** X axis label of a bucket: its Monday per week ("May 04"), month and year per month ("May 2026"). */
export const bucketLabel = (dateInterval: string, granularity: Granularity): string =>
  format(parseISO(dateInterval), granularity === 'weekly' ? 'MMM dd' : 'MMM yyyy');

/** The `toggle` object ExploreChart reads (shape of the Brand modals' useKPIToggle). */
export function buildChartToggle({
  data,
  metrics,
  visibleMetrics,
  granularity,
  isFetching,
  t,
}: {
  data: NonBrandMetricEvolutionResponse | undefined;
  metrics: MetricDefinition[];
  visibleMetrics: MetricKey[];
  granularity: Granularity;
  isFetching: boolean;
  t: (key: string) => string;
}) {
  const withLabels = (points: NonBrandMetricEvolutionPoint[]) =>
    points.map((point) => ({ ...point, date: bucketLabel(point.date_interval, granularity) }));
  return {
    stats: metrics.map(({ key, format: metricFormat, color }) => ({
      key,
      title: t(metricLabelKey(key)),
      format: metricFormat,
      color,
    })),
    statsData: data ? { time_series_data: withLabels(data.main) } : undefined,
    compareStatsData: { time_series_data: withLabels(data?.compare ?? []) },
    kpiToggle: visibleMetrics,
    isLoadingStats: isFetching,
    isLoadingCompareStats: false,
  };
}
